import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import type { BuildingMoveResult, BuildingSummary, ClientDetailDto } from "@klantpunt/shared";
import { Modal } from "../../app/Modal";
import { api, ApiError } from "../../lib/api";
import { useClients } from "../../lib/clientContext";
import { useToast } from "../../lib/toast";

const errMsg = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Er ging iets mis");
const todayIso = () => new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Amsterdam" }).format(new Date());

type MoveBuilding = Pick<BuildingSummary, "id" | "code" | "name" | "clientId" | "siteId">;

/** Na verplaatsen of terugzetten: lijsten, klanten en de gebouwen zelf opnieuw ophalen. */
export function useRefreshAfterMove() {
  const qc = useQueryClient();
  return (ids: string[]) => {
    void qc.invalidateQueries({ queryKey: ["buildings"] });
    void qc.invalidateQueries({ queryKey: ["clients"] });
    void qc.invalidateQueries({ queryKey: ["client"] });
    void qc.invalidateQueries({ queryKey: ["mjop"] });
    for (const id of ids) {
      void qc.invalidateQueries({ queryKey: ["building", id] });
      void qc.invalidateQueries({ queryKey: ["client-history", id] });
    }
  };
}

/**
 * Gebouwen naar een andere klant of locatie (docs/CLIENTS.md §5): los vanaf het gebouw of in bulk vanuit de
 * gebouwenlijst. Alles wat bij een gebouw hoort, gaat mee. Daarna 10 seconden "Ongedaan maken".
 */
export function MoveDialog({
  buildings,
  onClose,
  onMoved,
}: {
  buildings: MoveBuilding[];
  onClose: () => void;
  onMoved?: (r: BuildingMoveResult) => void;
}) {
  const toast = useToast();
  const refresh = useRefreshAfterMove();
  const clients = useClients();
  const one = buildings.length === 1 ? buildings[0] : null;
  const [clientId, setClientId] = useState(one?.clientId ?? "");
  const [siteId, setSiteId] = useState(one?.siteId ?? "");
  const [date, setDate] = useState(todayIso);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const detail = useQuery({
    queryKey: ["client", clientId],
    queryFn: () => api<ClientDetailDto>(`/clients/${clientId}`),
    enabled: Boolean(clientId),
  });
  const target = clients.data?.find((c) => c.id === clientId);
  const sources = new Set(buildings.map((b) => b.clientId));
  const fromUnassigned = buildings.every((b) => clients.data?.find((c) => c.id === b.clientId)?.isUnassigned);
  const unchanged = one && one.clientId === clientId && (one.siteId ?? "") === siteId;
  const what = one ? `${one.code} · ${one.name}` : `${buildings.length} gebouwen`;

  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (!clientId || unchanged) return;
    setBusy(true);
    setError(null);
    const ids = buildings.map((b) => b.id);
    try {
      const r = await api<BuildingMoveResult>("/buildings/move", {
        method: "POST",
        body: { buildingIds: ids, clientId, siteId: siteId || null, effectiveDate: date, reason },
      });
      refresh(ids);
      onMoved?.(r);
      onClose();
      const moveId = r.moveId;
      toast(
        r.moved === 0
          ? "Er veranderde niets: de gebouwen stonden al hier"
          : `${r.moved === 1 && one ? one.name : `${r.moved} gebouwen`} verplaatst naar ${target?.name ?? "de klant"}`,
        moveId
          ? {
              action: {
                label: "Ongedaan maken",
                onClick: () =>
                  void api(`/buildings/moves/${moveId}/undo`, { method: "POST" })
                    .then(() => {
                      refresh(ids);
                      toast("Verplaatsen ongedaan gemaakt");
                    })
                    .catch((err: unknown) => toast(errMsg(err))),
              },
            }
          : undefined,
      );
    } catch (err) {
      setError(errMsg(err));
      setBusy(false);
    }
  }

  return (
    <Modal
      title={one ? "Naar andere klant" : "Gebouwen verplaatsen"}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <span className="grow" />
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary"
            onClick={() => void save()}
            disabled={busy || !clientId || !!unchanged}
          >
            Verplaatsen
          </button>
        </>
      }
    >
      <form onSubmit={save}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <p>
          <strong>{what}</strong>
          {!one && sources.size === 1 && clients.data && (
            <span className="muted"> · nu bij {clients.data.find((c) => sources.has(c.id))?.name}</span>
          )}
        </p>
        <div className="grid2">
          <label className="field">
            <span className="label">Klant</span>
            <select
              value={clientId}
              onChange={(e) => {
                setClientId(e.target.value);
                setSiteId("");
              }}
              required
            >
              {!clientId && <option value="">Kies een klant</option>}
              {clients.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Locatie</span>
            <select
              value={siteId}
              onChange={(e) => setSiteId(e.target.value)}
              disabled={!clientId || !detail.data?.sites.length}
            >
              <option value="">{clientId ? "Geen locatie" : "Kies eerst een klant"}</option>
              {detail.data?.sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="label">Ingangsdatum</span>
            <input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)} required />
          </label>
          <label className="field">
            <span className="label">Reden (optioneel)</span>
            <input
              value={reason}
              maxLength={300}
              onChange={(e) => setReason(e.target.value)}
              placeholder="bijv. verkocht"
            />
          </label>
        </div>
        <p className="hint">
          Alles wat bij {one ? "het gebouw" : "de gebouwen"} hoort, gaat mee: verdiepingen, bevindingen, taken, assets,
          keuringen, meters, conditiemetingen en MJOP-posten. De klantgeschiedenis wordt bijgehouden.{" "}
          {fromUnassigned
            ? "Medewerkers met toegang tot een gebouw houden die."
            : "Toegang die medewerkers tot een los gebouw hadden, vervalt bij een andere klant."}
        </p>
        <button type="submit" hidden />
      </form>
    </Modal>
  );
}
